import {ofType} from 'redux-observable';
import {of, EMPTY, from, zip} from 'rxjs';
import {catchError, flatMap, mergeMap, tap} from 'rxjs/operators';
import {List} from 'immutable';
import i18next from 'i18next';
import {change} from 'redux-form';

import {combineEpics} from 'web-app/util/redux-observable';
import LocalStorageService from 'web-app/services/local-storage';
import * as SessionActions from 'web-app/react/session/actions';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import APIHelper from 'web-app/services/api-helper';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {ACCESS_TOKEN_KEY} from 'web-app/util/access-token-helpers';
import {buildUrlWithQueryParameters} from 'web-app/util/url';
import {
    SHOULD_ATTEMPT_TO_REESTABLISH_LAST_VISITED_ROUTE_QUERY_PARAM,
    JUST_SIGNED_UP_PARAM,
} from 'web-app/react/routes/constants';
import {AuthenticationStatus} from 'web-app/util/graphql/api-types';
import {purgeErrorLog} from 'web-app/react/routes/error-log/helpers';

import {type Account, fieldNames, type LoginFormData, INVALID_PIN} from './models';
import * as Api from './api';
import * as Actions from './actions';
import * as Queries from './queries';
import * as Helpers from './helpers';
import * as Constants from './constants';
import {
    type AuthenticateMutation,
    type AuthenticateMutationVariables,
    type AuthenticateWithPinMutation,
    type AuthenticateWithPinMutationVariables,
} from './__generated__/queries.api-types';
import {
    type AuthenticateWithTokenMutation,
    type AuthenticateWithTokenMutationVariables,
} from '../routes/login/__generated__/queries.api-types';
import {AuthenticateWithToken} from '../routes/login/queries';

const handleCredentialsLoginFailedCase = (
    payload: {data: LoginFormData; redirectConfig?: Actions.RedirectConfig},
    apiKeys?: string[],
    failedCaseError?: {
        error?: string;
        title?: string;
        description?: string;
    },
) => {
    // If there are more apiKeys -> check them
    if (apiKeys && apiKeys.length > 0) {
        return handleCredentialLoginWithGraphQLServers(payload, apiKeys);
    }
    // Otherwise -> return a failed action
    return of(Actions.credentialLoginFailed.action(failedCaseError));
};

const handleCredentialLoginWithGraphQLServers = (
    payload: {data: LoginFormData; redirectConfig?: Actions.RedirectConfig},
    apiKeys?: string[],
) => {
    const skipUserContext = !payload.data.shouldUseUserContext;

    const [apiKey, ...remainingApiKeys] = apiKeys ?? [];

    return from(
        getApolloClient(apiKey).mutate<AuthenticateMutation, AuthenticateMutationVariables>({
            mutation: Queries.Authenticate,
            variables: {
                email: payload.data.email,
                password: payload.data.password,
                deviceId: Helpers.getDeviceId(),
                // Mimic old login behavior without User Contexts unless explicitly enabled.
                legacy: skipUserContext,
            },
            context: {
                skipAuthentication: true,
            },
        }),
    ).pipe(
        flatMap(result => {
            if (!result?.data?.me?.authenticateWithPassword) {
                return handleCredentialsLoginFailedCase(payload, remainingApiKeys, {error: i18next.t('error.general')});
            }

            const authenticationResult = result.data.me.authenticateWithPassword;

            switch (authenticationResult.__typename) {
                case 'AuthenticationSucceeded': {
                    const {accessToken, deviceId} = authenticationResult;

                    return of(Actions.credentialLoginSuccess.action({accessToken}, payload.redirectConfig)).pipe(
                        tap(() => {
                            if (apiKey) {
                                APIHelper.setApiConfig(apiKey);
                            }
                            Helpers.postLoginChores(deviceId);
                        }),
                    );
                }
                case 'AuthenticationChallenged':
                    return zip(
                        of(change(Constants.FORM_NAMES.LOGIN_FORM, fieldNames('password'), '')),
                        of(
                            Actions.showChallenge.action({
                                challenge: authenticationResult,
                                loginTLD: apiKey,
                            }),
                        ),
                        of(Actions.credentialLoginAborted.action()),
                    ).pipe(flatMap(zipped => zipped));
                case 'AuthenticationFailed':
                    return handleCredentialsLoginFailedCase(payload, remainingApiKeys, {
                        title: authenticationResult.errorTitle,
                        description: authenticationResult.errorDetails,
                    });
                default:
                    return handleCredentialsLoginFailedCase(payload, remainingApiKeys, {
                        error: i18next.t('error.general'),
                    });
            }
        }),
        catchError(() => handleCredentialsLoginFailedCase(payload, remainingApiKeys)),
    );
};

const credentialLogin = action$ =>
    action$
        .ofType(Actions.credentialLogin.type)
        .mergeMap(({payload}: ReturnType<typeof Actions.credentialLogin.action>) => {
            // Mobile, known loginTLD
            if (payload.data.loginTLD) {
                return handleCredentialLoginWithGraphQLServers(payload, [payload.data.loginTLD]);
            }
            // Mobile, unknown loginTLD
            else if (payload.data.shouldPingAllServers) {
                return handleCredentialLoginWithGraphQLServers(payload, APIHelper.apiKeys);
            }
            // Desktop, unknown loginTLD
            else {
                return handleCredentialLoginWithGraphQLServers(payload);
            }
        });

const pinLogin = action$ =>
    action$.ofType(Actions.pinLogin.type).mergeMap(({payload}: ReturnType<typeof Actions.pinLogin.action>) => {
        return from(
            getApolloClient().mutate<AuthenticateWithPinMutation, AuthenticateWithPinMutationVariables>({
                mutation: Queries.AuthenticateWithPin,
                variables: {
                    deviceId: payload.deviceId,
                    pin: payload.pin,
                    loginId: payload.loginId,
                },
                context: {
                    skipAuthentication: true,
                },
            }),
        ).pipe(
            flatMap(result => {
                if (!result?.data?.me?.authenticateWithPin) {
                    return of(Actions.pinLoginFailed.action({error: i18next.t('error.general')}));
                }

                const authenticationResult = result.data.me.authenticateWithPin;

                switch (authenticationResult.__typename) {
                    case 'AuthenticationSucceeded': {
                        const {accessToken} = authenticationResult;
                        return of(
                            Actions.pinLoginSuccess.action({accessToken}, payload.loginId, payload.redirectConfig),
                        );
                    }
                    case 'AuthenticationFailed':
                    default:
                        return [
                            authenticationResult.errorDetails
                                ? // We use the error details since the error title is not very informative
                                  GlobalEventActions.updateError.action(authenticationResult.errorDetails)
                                : null,
                            Actions.pinLoginFailed.action({errorCode: INVALID_PIN}),

                            // If the user has no valid passwords login before pin, we re-fetch the available logins
                            // to show the login selector
                            authenticationResult.status === AuthenticationStatus.FailedNoValidLoginsBeforePin
                                ? Actions.fetchLoginsForDevice.action(payload.deviceId)
                                : null,
                        ].filter(hasValue);
                }
            }),
            catchError(() => of(Actions.pinLoginFailed.action())),
        );
    });

const tokenLogin = action$ =>
    action$.ofType(Actions.tokenLogin.type).mergeMap(({payload}: ReturnType<typeof Actions.tokenLogin.action>) => {
        return from(
            getApolloClient().mutate<AuthenticateWithTokenMutation, AuthenticateWithTokenMutationVariables>({
                mutation: AuthenticateWithToken,
                variables: {
                    token: payload.response.token,
                },
                context: {
                    skipAuthentication: true,
                },
            }),
        ).pipe(
            flatMap(result => {
                if (!result?.data?.me?.authenticateWithToken) {
                    // Otherwise -> return a failed action
                    return of(Actions.tokenLoginFailed.action({error: 'Unexpected error'}));
                }

                const accessToken = result.data.me.authenticateWithToken;

                return of(Actions.credentialLoginSuccess.action({accessToken}, payload.redirectConfig));
            }),
            catchError(error => of(Actions.tokenLoginFailed.action({error}))),
        );
    });

const credentialLoginForRedirect = action$ =>
    action$.pipe(
        ofType(Actions.credentialLoginForRedirect.type),
        flatMap(({payload}: ReturnType<typeof Actions.credentialLoginForRedirect.action>) => {
            return Api.loginsExists(payload.data.email).pipe(
                flatMap(response => {
                    return of(
                        Actions.credentialLogin.action(
                            {
                                ...payload.data,
                                shouldUseUserContext: Boolean(response.first()?.useUserContextAuthentication),
                            },
                            payload.redirectConfig,
                        ),
                    );
                }),
            );
        }),
    );

const fetchDeviceAccounts = action$ =>
    action$
        .ofType(Actions.fetchLoginsForDevice.type)
        .mergeMap(({payload}: ReturnType<typeof Actions.fetchLoginsForDevice.action>) =>
            Api.fetchDeviceAccounts(payload.deviceId)
                .map((response: Account[]) =>
                    Actions.fetchLoginsForDeviceSuccess.action(
                        List(response),
                        LocalStorageService.getFromLocalStorage(Constants.SELECTED_ACCOUNT_LOCAL_STORAGE_KEY),
                    ),
                )
                .catch(e => of(Actions.fetchLoginsForDeviceFailed.action(e))),
        );

const onLoginSuccess = action$ =>
    action$.pipe(
        ofType(Actions.credentialLoginSuccess.type, Actions.pinLoginSuccess.type, Actions.tokenLoginSuccess.type),
        tap(() => {
            purgeErrorLog();
        }),
        mergeMap(
            ({
                payload,
            }: ReturnType<
                | typeof Actions.credentialLoginSuccess.action
                | typeof Actions.pinLoginSuccess.action
                | typeof Actions.tokenLoginSuccess.action
            >) => of(SessionActions.didReceiveAccessToken(payload.response.accessToken)),
        ),
    );

const removeAccount = action$ =>
    action$.ofType(Actions.removeAccount.type).mergeMap(({payload}: ReturnType<typeof Actions.removeAccount.action>) =>
        Api.removeAccount(payload.deviceId, payload.loginId)
            .map(() => Actions.removeAccountSuccess.action(payload.loginId))
            .catch(e => of(Actions.removeAccountFailed.action(e))),
    );

const testEmailForLogins = action$ =>
    action$
        .ofType(Actions.testEmailForLogins.type)
        .map(({payload}: ReturnType<typeof Actions.testEmailForLogins.action>) => payload.email)
        .mergeMap(email => {
            return Api.loginsExists(email)
                .do(existingLogins => {
                    const existingLoginsToSave = existingLogins ? existingLogins.toArray() : [];
                    Helpers.saveExistingLogins(existingLoginsToSave);
                })
                .map(response => Actions.testEmailForLoginsSuccess.action(email, response))
                .catch(e => of(Actions.testEmailForLoginsFailed.action(e)));
        });

const loginRedirector = action$ =>
    action$
        .ofType(Actions.pinLoginSuccess.type, Actions.credentialLoginSuccess.type, Actions.tokenLoginSuccess.type)
        .mergeMap(
            ({
                payload,
            }: ReturnType<
                | typeof Actions.pinLoginSuccess.action
                | typeof Actions.credentialLoginSuccess.action
                | typeof Actions.tokenLoginSuccess.action
            >) => {
                /*
                1. Save the access token in local storage
                2. If a redirect url is passed use that
                3. Otherwise if we saved a transition from attempting to access the app use that
                4. Otherwise simply navigate to account route and the router will take over and handle navigation from there
             */
                LocalStorageService.saveToLocalStorage(ACCESS_TOKEN_KEY, payload.response.accessToken);

                const config = payload.redirectConfig;

                if (config && 'skipRedirect' in config) {
                    return EMPTY;
                }

                if (config && config.redirectUrl) {
                    window.location.replace(config.redirectUrl);
                } else {
                    const url = payload.response.justSignedUp
                        ? buildUrlWithQueryParameters('#/account', {
                              [SHOULD_ATTEMPT_TO_REESTABLISH_LAST_VISITED_ROUTE_QUERY_PARAM]: 'true',
                              [JUST_SIGNED_UP_PARAM]: payload.response.justSignedUp,
                          })
                        : buildUrlWithQueryParameters('#/account', {
                              [SHOULD_ATTEMPT_TO_REESTABLISH_LAST_VISITED_ROUTE_QUERY_PARAM]: 'true',
                          });

                    window.location.replace(url);
                }
                return EMPTY;
            },
        );

export default combineEpics(
    credentialLogin,
    pinLogin,
    tokenLogin,
    fetchDeviceAccounts,
    onLoginSuccess,
    removeAccount,
    testEmailForLogins,
    loginRedirector,
    credentialLoginForRedirect,
);
